import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtDate, slugify } from "../lib/utils-app";
import { articleMeters, articleConfidence, articleRegion } from "../lib/article-utils";
import Badge from "../components/Badge";
import ImpactMeter from "../components/ImpactMeter";
import SourceDrawer from "../components/SourceDrawer";
import AdBlock from "../components/AdBlock";
import CaseBriefCard from "../components/CaseBriefCard";
import Seo, { ld } from "../components/Seo";
import Img from "../components/Img";
import { ChevronRight, Clock, FileText, Share2, BookmarkPlus, Download, Sparkles, ShieldCheck, FileCheck2, Scale, AlertCircle, ChevronDown } from "lucide-react";

/* ==================== Schema LD ==================== */
const SchemaLD = ({ a }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": a.headline,
    "description": a.meta_description || a.summary,
    "datePublished": a.published_at,
    "dateModified": a.updated_at || a.published_at,
    "author": [{ "@type": "Person", "name": a.author }],
    "publisher": { "@type": "Organization", "name": "IndiaODR", "logo": { "@type": "ImageObject", "url": (process.env.REACT_APP_BACKEND_URL || "") + "/favicon.ico" } },
    "image": a.hero_image ? [a.hero_image] : [],
    "articleSection": a.category,
    "keywords": (a.tags || []).join(", "),
    "mainEntityOfPage": (typeof window !== "undefined" ? window.location.href : ""),
  };
  const faq = a.faq && a.faq.length > 0 ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": a.faq.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
  } : null;
  return (<>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
    {faq && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faq) }} />}
  </>);
};

const setMeta = (name, content) => {
  // Deprecated — replaced by react-helmet-async via Seo component.
  if (!content) return;
};

/* ==================== FAQ Accordion ==================== */
const FAQ = ({ items = [] }) => {
  const [open, setOpen] = useState(0);
  if (!items.length) return null;
  return (
    <div className="mt-12" data-testid="article-faq">
      <div className="eyebrow mb-4">Frequently Asked</div>
      <div className="space-y-2">
        {items.map((f, i) => (
          <div key={i} className="border border-[var(--io-border)] rounded-md overflow-hidden bg-[var(--io-bg-2)]">
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`faq-toggle-${i}`}>
              <span className="font-sans font-medium text-[15px] text-[var(--io-text)] pr-4">{f.q}</span>
              <ChevronDown size={16} className={`text-[var(--io-text-2)] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <div className="px-5 pb-5 text-[14px] text-[var(--io-text)] leading-relaxed">{f.a}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================== Main Page ==================== */
export default function Article() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setA(null); setErr(false);
    api.get(`/articles/by-slug/${slug}`)
      .then((r) => {
        setA(r.data.article); setRelated(r.data.related || []);
      })
      .catch(() => setErr(true));
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? (h.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-24" data-testid="article-not-found">
      <div className="eyebrow" style={{ color: "#EF4444" }}>404</div>
      <h1 className="font-serif text-5xl mt-3 text-[var(--io-text)]">Article not found</h1>
      <button onClick={() => nav("/")} className="mt-8 btn btn-ghost" data-testid="article-back-home">← Return home</button>
    </div>
  );
  if (!a) return <div className="max-w-3xl mx-auto px-6 py-24 text-[var(--io-muted)]">Loading brief…</div>;

  const meters = articleMeters(a);
  const confidence = articleConfidence(a);
  const sourceCount = (a.source_refs || []).length;
  const risk = a.legal_risk || "low";
  const riskVariant = risk === "high" ? "risk_high" : risk === "medium" ? "risk_med" : "risk_low";

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: a.headline, url }); } catch {} }
    else { navigator.clipboard.writeText(url); }
  };

  return (
    <article data-testid="article-page">
      <Seo
        title={a.meta_title || a.headline}
        description={a.meta_description || a.summary}
        path={`/article/${a.slug}`}
        image={a.hero_image}
        type="article"
        jsonLd={ld.breadcrumb(typeof window !== "undefined" ? window.location.origin : "", [
          { name: "Home", path: "/" },
          { name: a.category, path: `/category/${slugify(a.category)}` },
          { name: a.headline },
        ])}
      />
      <SchemaLD a={a} />

      {/* Reading progress */}
      <div className="fixed top-0 left-0 right-0 z-50" aria-hidden="true">
        <div className="reading-rule" style={{ width: `${progress}%` }} />
      </div>

      {/* ==== Breadcrumb ==== */}
      <nav className="max-w-[1380px] mx-auto px-4 md:px-8 pt-8 flex items-center gap-1.5 text-[11px] font-mono text-[var(--io-muted)]" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[var(--io-gold)]">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/category/${slugify(a.category)}`} className="hover:text-[var(--io-gold)]">{a.category}</Link>
        <ChevronRight size={12} />
        <span className="text-[var(--io-text-2)] truncate max-w-[420px]">{a.headline}</span>
      </nav>

      {/* ==== HEADER ==== */}
      <header className="max-w-[1380px] mx-auto px-4 md:px-8 pt-6 pb-10">
        <div className="flex items-center gap-2 mb-5">
          <span className="eyebrow">{articleRegion(a)}</span>
          <span className="w-1 h-1 rounded-full bg-[var(--io-muted)]" />
          <span className="eyebrow" style={{ color: "#D4AF37" }}>{a.category}</span>
        </div>

        <h1 className="font-serif text-[38px] md:text-[56px] lg:text-[64px] leading-[1.03] tracking-tight text-[var(--io-text)] max-w-[24ch]">{a.headline}</h1>
        <p className="mt-6 text-[18px] md:text-[20px] leading-[1.6] text-[var(--io-text)] max-w-[68ch] font-sans">{a.summary}</p>

        {/* Byline & meta */}
        <div className="mt-7 pt-5 border-t border-[var(--io-border)] flex flex-wrap gap-x-6 gap-y-2 font-mono text-[12px] text-[var(--io-text-2)]">
          <span>By <span className="text-[var(--io-text)] font-sans font-medium">{a.author}</span></span>
          <span>Published <span className="text-[var(--io-text)]">{fmtDate(a.published_at)}</span></span>
          {a.updated_at && a.updated_at !== a.published_at && <span>Updated <span className="text-[var(--io-text)]">{fmtDate(a.updated_at)}</span></span>}
          <span className="inline-flex items-center gap-1"><Clock size={12}/> {a.read_minutes || 4} min read</span>
          <span>{sourceCount} source{sourceCount !== 1 ? "s" : ""}</span>
        </div>

        {/* Trust bar */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="trust-bar">
          <Badge variant="ai" />
          <Badge variant="human" />
          <Badge variant="source" />
          <Badge variant="legal" />
          <Badge variant="correction" />
          {risk !== "low" && <Badge variant={riskVariant} />}
        </div>
      </header>

      {/* Hero image */}
      {a.hero_image && (
        <div className="max-w-[1380px] mx-auto px-4 md:px-8 mb-10">
          <div className="rounded-lg overflow-hidden border border-[var(--io-border)]">
            <Img src={a.hero_image} className="w-full max-h-[560px] object-cover" w={1400} priority alt={a.headline} />
          </div>
        </div>
      )}

      {/* ==== TWO-COLUMN BODY ==== */}
      <div className="max-w-[1380px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-8 prose-io">
          {(a.article_type === "case_brief" || a.case_brief) && <CaseBriefCard brief={a.case_brief} />}

          <section data-testid="article-summary">
            <div className="eyebrow mb-2">Executive Summary</div>
            <p className="!text-[19px] !text-[#E2E8F0] border-l-2 border-[var(--io-gold)] pl-5 !leading-[1.7]">{a.summary}</p>
          </section>

          <section className="mt-10">
            <h2>What happened</h2>
            {a.what_happened.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
          </section>

          {a.key_legal_issue && <section className="mt-2"><h2>Key legal issue</h2>{a.key_legal_issue.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}</section>}
          {a.why_it_matters && <section className="mt-2"><h2>Why it matters</h2>{a.why_it_matters.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}</section>}

          {a.timeline && (
            <section className="mt-2">
              <h2>Timeline</h2>
              <div className="border-l-2 border-[var(--io-border)] pl-6 space-y-3">
                {a.timeline.split("\n").filter(Boolean).map((line, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[29px] top-2 w-2 h-2 rounded-full bg-[var(--io-gold)]" />
                    <p className="!mb-0">{line.replace(/^-\s*/, "")}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {a.business_impact && <section className="mt-2"><h2>Business impact</h2>{a.business_impact.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}</section>}
          {a.india_odr_relevance && <section className="mt-2"><h2>India &amp; ODR relevance</h2>{a.india_odr_relevance.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}</section>}

          <SourceDrawer sources={a.source_refs} />
          <FAQ items={a.faq} />

          {/* Tags */}
          {a.tags && a.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="article-tags">
              {a.tags.map((t) => (
                <Link key={t} to={`/search?q=${encodeURIComponent(t)}`}
                  className="io-badge hover:border-[var(--io-gold)] hover:text-[var(--io-gold)]" data-testid={`tag-${t}`}>{t}</Link>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-10 p-5 border border-[var(--io-border)] bg-[var(--io-bg-2)] rounded-md" data-testid="article-disclaimer">
            <div className="flex items-start gap-3">
              <Scale size={16} className="text-[var(--io-gold)] mt-0.5 shrink-0" />
              <div>
                <div className="kicker mb-1">Legal information only</div>
                <p className="!mb-0 !text-[13px] !text-[var(--io-text-2)] !leading-relaxed">{a.disclaimer}</p>
                <Link to="/correction-policy" className="mt-2 inline-block text-[11px] font-mono text-[#3B82F6] hover:text-[var(--io-gold)]">See our correction policy →</Link>
              </div>
            </div>
          </div>
        </div>

        {/* ==== STICKY INTELLIGENCE SIDEBAR ==== */}
        <aside className="lg:col-span-4" data-testid="intelligence-sidebar">
          <div className="lg:sticky lg:top-28 space-y-5">
            {/* Sidebar: Article Intelligence */}
            <div className="io-card-el p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="eyebrow">Article Intelligence</div>
                <Sparkles size={14} className="text-[var(--io-gold)]" />
              </div>
              <dl className="space-y-2.5 text-[12px] font-mono">
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Region</dt><dd className="text-[var(--io-text)]">{articleRegion(a)}</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Topic</dt><dd className="text-[var(--io-text)]">{a.category}</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Source count</dt><dd className="text-[var(--io-text)]">{sourceCount}</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Reading time</dt><dd className="text-[var(--io-text)]">{a.read_minutes || 4} min</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Last updated</dt><dd className="text-[var(--io-text)]">{fmtDate(a.updated_at || a.published_at)}</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">AI confidence</dt><dd className="text-[#10B981]">{confidence}%</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Review status</dt><dd className="text-[var(--io-gold)]">Published</dd></div>
                <div className="flex justify-between"><dt className="text-[var(--io-muted)]">Legal sensitivity</dt><dd className={risk === "high" ? "text-[#EF4444]" : risk === "medium" ? "text-[#F59E0B]" : "text-[#10B981]"}>{risk}</dd></div>
              </dl>

              <div className="mt-5 pt-4 border-t border-[var(--io-border)] space-y-4">
                <ImpactMeter label="Legal impact" value={meters.legal} tone="gold" testId="meter-legal" />
                <ImpactMeter label="Business impact" value={meters.business} tone="blue" testId="meter-business" />
                <ImpactMeter label="India relevance" value={meters.india} tone="green" testId="meter-india" />
                <ImpactMeter label="ODR relevance" value={meters.odr} tone="amber" testId="meter-odr" />
              </div>
            </div>

            {/* Actions */}
            <div className="io-card-el p-5" data-testid="article-actions">
              <div className="eyebrow mb-3">Actions</div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={share} className="btn btn-ghost !py-2 !px-2 justify-center !text-[11px]" data-testid="share-btn"><Share2 size={14}/> Share</button>
                <button className="btn btn-ghost !py-2 !px-2 justify-center !text-[11px]" data-testid="save-btn"><BookmarkPlus size={14}/> Save</button>
                <button onClick={() => window.print()} className="btn btn-ghost !py-2 !px-2 justify-center !text-[11px]" data-testid="download-btn"><Download size={14}/> Brief</button>
              </div>
            </div>

            {/* Subscribe */}
            <div className="io-card-el p-5 relative overflow-hidden">
              <div className="absolute inset-0 intel-grid opacity-30 pointer-events-none"/>
              <div className="relative">
                <div className="eyebrow" style={{ color: "#D4AF37" }}>Weekly Digest</div>
                <h4 className="font-serif text-[18px] mt-1 text-[var(--io-text)]">Never miss a ruling.</h4>
                <p className="text-[12px] text-[var(--io-text-2)] mt-1.5">Friday's arbitration developments in your inbox.</p>
                <Link to="/digest" className="btn btn-primary w-full justify-center mt-4 !py-2 !text-[12px]" data-testid="sidebar-subscribe">Subscribe →</Link>
              </div>
            </div>

            {/* PrivateCourt.ai sponsored slot */}
            <AdBlock variant="sidebar" />


            {/* Related */}
            {related.length > 0 && (
              <div className="io-card-el p-5" data-testid="related-articles">
                <div className="eyebrow mb-3">Related briefs</div>
                <div className="divide-y divide-[var(--io-border)]">
                  {related.map((r) => (
                    <Link key={r.id} to={`/article/${r.slug}`} className="block py-3 group" data-testid={`related-${r.slug}`}>
                      <div className="font-mono text-[10px] text-[var(--io-gold)]">{r.category}</div>
                      <h5 className="font-serif text-[15px] mt-1 text-[var(--io-text)] group-hover:text-[var(--io-gold)] leading-snug">{r.headline}</h5>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </article>
  );
}
